import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { decodeProtectedData, PROTECTED_DATA } from '../../utils/obfuscation';
import ContactForm from './components/ContactForm';
import ContactInfoCard from './components/ContactInfoCard';
import SocialLinks from './components/SocialLinks';
import type {
  ProjectTypeOption,
  BudgetOption,
  ContactMethodOption,
  ContactInfo,
  SocialLink,
} from './types';

const ContactSection = () => {
  const { t } = useTranslation();

  const projectTypes: ProjectTypeOption[] = [
    {
      value: 'web-app',
      label: t('contact.projectTypes.webApp.label'),
      description: t('contact.projectTypes.webApp.description'),
    },
    {
      value: 'ecommerce',
      label: t('contact.projectTypes.ecommerce.label'),
      description: t('contact.projectTypes.ecommerce.description'),
    },
    {
      value: 'api',
      label: t('contact.projectTypes.api.label'),
      description: t('contact.projectTypes.api.description'),
    },
    {
      value: 'mobile',
      label: t('contact.projectTypes.mobile.label'),
      description: t('contact.projectTypes.mobile.description'),
    },
    {
      value: 'consulting',
      label: t('contact.projectTypes.consulting.label'),
      description: t('contact.projectTypes.consulting.description'),
    },
    {
      value: 'maintenance',
      label: t('contact.projectTypes.maintenance.label'),
      description: t('contact.projectTypes.maintenance.description'),
    },
  ];

  const budgetOptions: BudgetOption[] = [
    { value: 'small', label: t('contact.budgets.small') },
    { value: 'medium', label: t('contact.budgets.medium') },
    { value: 'large', label: t('contact.budgets.large') },
    { value: 'enterprise', label: t('contact.budgets.enterprise') },
    { value: 'flexible', label: t('contact.budgets.flexible') },
  ];

  const contactMethods: ContactMethodOption[] = [
    { value: 'email', label: t('contact.contactMethods.email') },
    { value: 'phone', label: t('contact.contactMethods.phone') },
    { value: 'video', label: t('contact.contactMethods.video') },
    { value: 'any', label: t('contact.contactMethods.any') },
  ];

  const contactInfo: ContactInfo[] = [
    {
      icon: 'Mail',
      label: t('contact.labels.email'),
      value: decodeProtectedData(PROTECTED_DATA.email),
      href: decodeProtectedData(PROTECTED_DATA.emailHref),
    },
    {
      icon: 'Phone',
      label: t('contact.labels.phone'),
      value: decodeProtectedData(PROTECTED_DATA.phone),
      href: decodeProtectedData(PROTECTED_DATA.phoneHref),
    },
    {
      icon: 'MapPin',
      label: t('footer.contacts.location'),
      value: t('footer.contacts.location') === 'Location' ? decodeProtectedData(PROTECTED_DATA.addressEn) : decodeProtectedData(PROTECTED_DATA.addressAr),
      href: 'https://maps.google.com/?q=Bir+El-Djir,Oran,Algeria',
    },
  ];

  const socialLinks: SocialLink[] = [
    {
      name: 'GitHub',
      icon: 'Github',
      url: 'https://github.com/BMTmohammedtaha',
      label: 'Visit GitHub profile',
    },
    {
      name: 'LinkedIn',
      icon: 'Linkedin',
      url: 'https://linkedin.com/in/bmt-mohammedtaha/',
      label: 'Connect on LinkedIn',
    },
    {
      name: 'X',
      icon: 'X',
      url: 'https://x.com/BmtMohammedTaha',
      label: 'Follow on X',
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('contact.title')} {t('contact.amazing')} - {t('hero.name')}</title>
        <meta
          name="description"
          content={t('contact.subtitle')}
        />
        <meta
          name="keywords"
          content="contact developer, hire full-stack developer, Laravel developer, React developer, web development services"
        />
      </Helmet>

      <div className="min-h-screen gradient-background">
        <Header />

        <main className="pt-24 pb-16 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {t('contact.title')}{' '}
                <span className="text-accent glow-text">
                  {t('contact.amazing')}
                </span>
              </h1>
              <p className="text-lg md:text-xl text-white/65 max-w-3xl mx-auto">
                {t('contact.subtitle')}
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-16">
              <div className="lg:col-span-2">
                <div className="glass-card rounded-2xl p-6 md:p-8">
                  <div className="mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                      {t('contact.startProject')}
                    </h2>
                    <p className="text-white/60">
                      {t('contact.formSubtitle')}
                    </p>
                  </div>
                  <ContactForm
                    projectTypes={projectTypes}
                    budgetOptions={budgetOptions}
                    contactMethods={contactMethods}
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="glass-card rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-white mb-6">{t('contact.infoTitle')}</h3>
                  <div className="space-y-4">
                    {contactInfo.map((info) => (
                      <ContactInfoCard key={info.label} info={info} />
                    ))}
                  </div>
                </div>

                <div className="glass-card rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-white mb-6">{t('contact.connectTitle')}</h3>
                  <SocialLinks links={socialLinks} />
                </div>

                <div className="gradient-cta rounded-2xl p-6 text-center">
                  <h3 className="text-xl font-bold text-white mb-2">{t('contact.quickResponseTitle')}</h3>
                  <p className="text-sm text-white/80 mb-4">
                    {t('contact.quickResponseDesc')}
                  </p>
                  <div className="flex items-center justify-center gap-2 text-sm text-white">
                    <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                    {t('contact.available')}
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                {t('contact.preferDirect')}
              </h2>
              <p className="text-white/60 mb-8 max-w-2xl mx-auto">
                {t('contact.preferDirectDesc')}
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href={decodeProtectedData(PROTECTED_DATA.emailHref)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl gradient-cta text-white font-semibold hover:scale-105 hover:shadow-brand-lg transition-all duration-300"
                >
                  {t('contact.sendEmail')}
                </a>
                <a
                  href="https://calendly.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl glass border border-white/20 text-white font-semibold hover:border-accent/50 hover:bg-white/10 transition-all duration-300"
                >
                  {t('contact.scheduleCall')}
                </a>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ContactSection;